import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
    PlusCircle, Sparkles, Archive, ArchiveRestore,
    Trash2, RotateCcw, Ban, BookOpen, Pencil, MoreHorizontal,
    Globe, FileEdit, Lock, Unlock, CalendarDays,
} from "lucide-react";
import ProgramScheduleDialog from "@/components/admin/ProgramScheduleDialog";
import { toast } from "sonner";
import CreateProgramWizard from "@/components/wizard/CreateProgramWizard";
import BulkActionBar from "@/components/BulkActionBar";
import ProgramEditDialog from "@/components/admin/ProgramEditDialog";
import TuitionBadge from "@/components/TuitionBadge";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function AdminPrograms() {
    const [activePrograms, setActivePrograms] = useState([]);
    const [trashedPrograms, setTrashedPrograms] = useState([]);
    const [users, setUsers] = useState([]);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [generatingId, setGeneratingId] = useState(null);
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [scheduleProgram, setScheduleProgram] = useState(null);
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [purgeTarget, setPurgeTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [tab, setTab] = useState("published");
    const [selectedActive, setSelectedActive] = useState(new Set());
    const [selectedTrash, setSelectedTrash] = useState(new Set());
    const [bulkConfirm, setBulkConfirm] = useState(null); // { action, title, desc, destructive }

    const load = useCallback(async () => {
        const [active, trash, u] = await Promise.all([
            api.get("/programs?include_archived=true"),
            api.get("/programs?include_deleted=true"),
            api.get("/admin/users"),
        ]);
        setActivePrograms(active.data);
        setTrashedPrograms(trash.data);
        setUsers(u.data.filter((x) => x.role === "instructor"));
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    // Clear selection when switching tabs
    useEffect(() => {
        setSelectedActive(new Set());
        setSelectedTrash(new Set());
    }, [tab]);

    const toggleSel = (set, setter, id) => {
        const next = new Set(set);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setter(next);
    };
    const toggleAll = (items, set, setter) => {
        if (set.size === items.length) setter(new Set());
        else setter(new Set(items.map((p) => p.id)));
    };

    const generateAI = async (programId) => {
        setGeneratingId(programId);
        try {
            const { data } = await api.post("/ai/generate-program-content", {
                program_id: programId,
                num_chapters: 2,
                lessons_per_chapter: 2,
            });
            toast.success(`${data.chapters_created} AI chapters generated. Instructor can review.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally {
            setGeneratingId(null);
        }
    };

    const toggleArchive = async (program) => {
        try {
            await api.patch(`/admin/programs/${program.id}`, {
                is_archived: !program.is_archived,
            });
            toast.success(program.is_archived ? "Program restored" : "Program archived");
            setArchiveTarget(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
            setArchiveTarget(null);
        }
    };

    const togglePublish = async (program) => {
        try {
            await api.patch(`/admin/programs/${program.id}`, {
                is_published: !program.is_published,
            });
            toast.success(
                program.is_published
                    ? "Program moved to draft — hidden from the public catalog"
                    : "Program published — now visible on the website",
            );
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    const toggleLock = async (program) => {
        try {
            await api.patch(`/admin/programs/${program.id}`, {
                is_locked: !program.is_locked,
            });
            toast.success(
                program.is_locked
                    ? "Program unlocked — instructors and SMEs can edit again"
                    : "Program locked — only admins can edit until unlocked",
            );
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    const singleAction = async (url, method, successMsg) => {
        try {
            await api.request({ url, method });
            toast.success(successMsg);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    // Bulk action runner
    const runBulk = async () => {
        if (!bulkConfirm) return;
        const ids = Array.from(
            bulkConfirm.scope === "trash" ? selectedTrash : selectedActive,
        );
        try {
            const { data } = await api.post("/admin/programs/bulk", {
                ids,
                action: bulkConfirm.action,
            });
            if (data.failed) {
                toast.warning(`${data.ok} succeeded, ${data.failed} failed.`);
            } else {
                toast.success(`${data.ok} program(s) updated.`);
            }
            setSelectedActive(new Set());
            setSelectedTrash(new Set());
            setBulkConfirm(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk action failed");
            setBulkConfirm(null);
        }
    };

    const renderActiveCard = (p) => {
        const checked = selectedActive.has(p.id);
        return (
            <Card
                key={p.id}
                className={`border-border transition-colors ${checked ? "ring-2 ring-accent" : ""}`}
                data-testid={`program-card-${p.id}`}
            >
                <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <Checkbox
                            checked={checked}
                            onCheckedChange={() =>
                                toggleSel(selectedActive, setSelectedActive, p.id)
                            }
                            className="mt-1"
                            data-testid={`check-program-${p.id}`}
                            aria-label={`Select ${p.title}`}
                        />
                        <div className="flex items-center gap-2 flex-wrap flex-1">
                            <Badge variant="outline" className="capitalize">
                                {p.subject}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                                {p.level}
                            </Badge>
                            {p.is_published ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`badge-published-${p.id}`}>
                                    Published
                                </Badge>
                            ) : (
                                <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`badge-draft-${p.id}`}>
                                    Draft
                                </Badge>
                            )}
                            {p.is_archived && <Badge variant="outline">Archived</Badge>}
                            {p.is_locked && (
                                <Badge className="bg-slate-800 text-white border border-slate-900" data-testid={`badge-locked-${p.id}`}>
                                    <Lock className="h-3 w-3 mr-1" />
                                    Locked
                                </Badge>
                            )}
                        </div>
                    </div>
                    <h3 className="font-display text-xl text-primary">{p.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {p.description}
                    </p>
                    <div className="text-sm mt-3 text-foreground/80">
                        <TuitionBadge
                            domestic={p.price}
                            international={p.price_international}
                            compact
                        />
                        {" · "}
                        {users.find((u) => u.id === p.instructor_id)?.name || (
                            <span className="italic">No instructor</span>
                        )}
                    </div>
                    <div className="mt-4 flex gap-2 flex-wrap">
                        <Button
                            size="sm"
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => setEditTarget(p)}
                            data-testid={`edit-details-${p.id}`}
                        >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit details
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            asChild
                            data-testid={`edit-program-${p.id}`}
                        >
                            <Link to={`/instructor/programs/${p.id}`}>Edit content</Link>
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => generateAI(p.id)}
                            disabled={generatingId === p.id}
                            data-testid={`admin-ai-gen-${p.id}`}
                        >
                            <Sparkles className="h-3 w-3 mr-1" />
                            {generatingId === p.id ? "Generating…" : "AI content"}
                        </Button>
                        <Button size="sm" variant="outline" asChild>
                            <Link to={`/programs/${p.id}`}>View</Link>
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="px-2"
                                    data-testid={`program-more-${p.id}`}
                                    aria-label="More actions"
                                >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                    onClick={() => togglePublish(p)}
                                    data-testid={`publish-toggle-${p.id}`}
                                >
                                    {p.is_published ? (
                                        <>
                                            <FileEdit className="h-3.5 w-3.5 mr-2" />
                                            Move to draft
                                        </>
                                    ) : (
                                        <>
                                            <Globe className="h-3.5 w-3.5 mr-2" />
                                            Publish
                                        </>
                                    )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => setScheduleProgram(p)}
                                    data-testid={`schedule-toggle-${p.id}`}
                                >
                                    <CalendarDays className="h-3.5 w-3.5 mr-2" />
                                    Manage schedule
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => toggleLock(p)}
                                    data-testid={`lock-toggle-${p.id}`}
                                >
                                    {p.is_locked ? (
                                        <>
                                            <Unlock className="h-3.5 w-3.5 mr-2" />
                                            Unlock for editing
                                        </>
                                    ) : (
                                        <>
                                            <Lock className="h-3.5 w-3.5 mr-2" />
                                            Lock from edits
                                        </>
                                    )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => setArchiveTarget(p)}
                                    data-testid={`archive-toggle-${p.id}`}
                                >
                                    {p.is_archived ? (
                                        <>
                                            <ArchiveRestore className="h-3.5 w-3.5 mr-2" />
                                            Unarchive
                                        </>
                                    ) : (
                                        <>
                                            <Archive className="h-3.5 w-3.5 mr-2" />
                                            Archive
                                        </>
                                    )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => setDeleteTarget(p)}
                                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                                    data-testid={`delete-program-${p.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </CardContent>
            </Card>
        );
    };

    const renderTrashCard = (p) => {
        const checked = selectedTrash.has(p.id);
        return (
            <Card
                key={p.id}
                className={`border-border bg-destructive/5 transition-colors ${
                    checked ? "ring-2 ring-destructive" : ""
                }`}
                data-testid={`trash-card-${p.id}`}
            >
                <CardContent className="p-6">
                    <div className="flex items-start gap-3 mb-2">
                        <Checkbox
                            checked={checked}
                            onCheckedChange={() =>
                                toggleSel(selectedTrash, setSelectedTrash, p.id)
                            }
                            className="mt-1"
                            data-testid={`check-trash-${p.id}`}
                            aria-label={`Select ${p.title}`}
                        />
                        <div className="flex items-center gap-2 flex-wrap flex-1">
                            <Badge className="bg-destructive/15 text-destructive border border-destructive/30">
                                <Trash2 className="h-3 w-3 mr-1" />
                                In trash
                            </Badge>
                            {p.subject && (
                                <Badge variant="outline" className="capitalize">
                                    {p.subject}
                                </Badge>
                            )}
                        </div>
                    </div>
                    <h3 className="font-display text-xl text-primary">{p.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {p.description}
                    </p>
                    {p.deleted_at && (
                        <div className="text-xs text-muted-foreground mt-2">
                            Deleted {formatDate(p.deleted_at)}
                        </div>
                    )}
                    <div className="mt-4 flex gap-2 flex-wrap">
                        <Button
                            size="sm"
                            onClick={() => setRestoreTarget(p)}
                            className="bg-primary hover:bg-primary/90"
                            data-testid={`restore-program-${p.id}`}
                        >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Restore
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => setPurgeTarget(p)}
                            data-testid={`purge-program-${p.id}`}
                        >
                            <Ban className="h-3 w-3 mr-1" />
                            Delete forever
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    };

    const publishedPrograms = useMemo(
        () => activePrograms.filter((p) => p.is_published),
        [activePrograms],
    );
    const draftPrograms = useMemo(
        () => activePrograms.filter((p) => !p.is_published),
        [activePrograms],
    );
    const currentListForTab = tab === "published" ? publishedPrograms
        : tab === "draft" ? draftPrograms
        : activePrograms;
    const allActiveSelected = useMemo(
        () =>
            currentListForTab.length > 0 && selectedActive.size === currentListForTab.length,
        [currentListForTab.length, selectedActive.size],
    );
    const allTrashSelected = useMemo(
        () =>
            trashedPrograms.length > 0 && selectedTrash.size === trashedPrograms.length,
        [trashedPrograms.length, selectedTrash.size],
    );

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 pb-24" data-testid="admin-programs-page">
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="flex items-end justify-between mt-2 mb-8 flex-wrap gap-3 anim-fade-up">
                <div>
                    <div className="inline-flex items-center gap-1.5 chip chip-accent">
                        <BookOpen className="h-3 w-3" />
                        Programs
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-primary mt-3 tracking-tight">
                        Program Management
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                        Curate the catalogue, set pricing, archive what&rsquo;s no longer running,
                        or kick off the AI authoring wizard.
                    </p>
                </div>
                <Button
                    className="bg-primary hover:bg-primary/90 gap-1.5"
                    onClick={() => setWizardOpen(true)}
                    data-testid="create-program-btn"
                >
                    <PlusCircle className="h-4 w-4" />
                    New program
                </Button>
            </div>

            <CreateProgramWizard
                open={wizardOpen}
                onOpenChange={setWizardOpen}
                instructors={users}
                onCreated={load}
            />

            <ProgramEditDialog
                open={!!editTarget}
                onOpenChange={(o) => !o && setEditTarget(null)}
                program={editTarget}
                instructors={users}
                onSaved={() => {
                    setEditTarget(null);
                    load();
                }}
            />

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="programs-tabs">
                    <TabsTrigger value="published" data-testid="tab-published">
                        Published
                        <Badge variant="outline" className="ml-2">
                            {publishedPrograms.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="draft" data-testid="tab-draft">
                        Draft
                        <Badge variant="outline" className="ml-2">
                            {draftPrograms.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="trash" data-testid="tab-trash">
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Trash
                        <Badge variant="outline" className="ml-2">
                            {trashedPrograms.length}
                        </Badge>
                    </TabsTrigger>
                </TabsList>

                {["published", "draft"].map((kind) => {
                    const list = kind === "published" ? publishedPrograms : draftPrograms;
                    const emptyLabel = kind === "published"
                        ? "No published programs yet. Draft a program and publish it to make it available on the public catalog."
                        : "No drafts. Programs you create start as drafts until you publish them.";
                    return (
                        <TabsContent key={kind} value={kind} className="mt-6">
                            {list.length === 0 ? (
                                <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-sm">
                                    {emptyLabel}
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border">
                                        <Checkbox
                                            checked={allActiveSelected}
                                            onCheckedChange={() =>
                                                toggleAll(list, selectedActive, setSelectedActive)
                                            }
                                            data-testid={`select-all-${kind}`}
                                            aria-label={`Select all ${kind} programs`}
                                        />
                                        <span className="text-sm text-muted-foreground">
                                            {selectedActive.size === 0
                                                ? `Select all ${list.length}`
                                                : `${selectedActive.size} of ${list.length} selected`}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {list.map(renderActiveCard)}
                                    </div>
                                </>
                            )}
                        </TabsContent>
                    );
                })}

                <TabsContent value="trash" className="mt-6">
                    {trashedPrograms.length === 0 ? (
                        <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-sm">
                            Trash is empty. Deleted programs can be restored from here within
                            their retention window.
                        </div>
                    ) : (
                        <>
                            <p className="text-sm text-muted-foreground mb-4">
                                Trashed programs are hidden from students and the public catalog.
                                Restore to make them active again, or permanently delete them.
                                Permanent deletion also removes chapters, lessons, quizzes,
                                enrollments, and certificates — it cannot be undone.
                            </p>
                            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border">
                                <Checkbox
                                    checked={allTrashSelected}
                                    onCheckedChange={() =>
                                        toggleAll(trashedPrograms, selectedTrash, setSelectedTrash)
                                    }
                                    data-testid="select-all-trash"
                                    aria-label="Select all trashed programs"
                                />
                                <span className="text-sm text-muted-foreground">
                                    {selectedTrash.size === 0
                                        ? `Select all ${trashedPrograms.length}`
                                        : `${selectedTrash.size} of ${trashedPrograms.length} selected`}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {trashedPrograms.map(renderTrashCard)}
                            </div>
                        </>
                    )}
                </TabsContent>
            </Tabs>

            {/* Bulk action bars */}
            {(tab === "published" || tab === "draft") && (
                <BulkActionBar
                    testId="bulk-bar-active"
                    count={selectedActive.size}
                    onClear={() => setSelectedActive(new Set())}
                    actions={[
                        {
                            key: "archive",
                            label: "Archive",
                            Icon: Archive,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "active",
                                    action: "archive",
                                    title: "Archive selected programs?",
                                    desc: `${selectedActive.size} program(s) will be hidden from the public catalog. Existing enrollments keep access. You can unarchive any time.`,
                                }),
                        },
                        {
                            key: "delete",
                            label: "Move to trash",
                            Icon: Trash2,
                            destructive: true,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "active",
                                    action: "delete",
                                    title: "Move selected programs to trash?",
                                    desc: `${selectedActive.size} program(s) will be hidden from students and the public catalog. You can restore them from the Trash tab at any time.`,
                                    destructive: true,
                                }),
                        },
                    ]}
                />
            )}

            {tab === "trash" && (
                <BulkActionBar
                    testId="bulk-bar-trash"
                    count={selectedTrash.size}
                    onClear={() => setSelectedTrash(new Set())}
                    actions={[
                        {
                            key: "restore",
                            label: "Restore",
                            Icon: RotateCcw,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "trash",
                                    action: "restore",
                                    title: "Restore selected programs?",
                                    desc: `${selectedTrash.size} program(s) will be restored to your Active list, unpublished until you publish them.`,
                                }),
                        },
                        {
                            key: "purge",
                            label: "Delete forever",
                            Icon: Ban,
                            destructive: true,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "trash",
                                    action: "purge",
                                    title: "Delete these programs forever?",
                                    desc: `${selectedTrash.size} program(s) and ALL of their chapters, lessons, quizzes, enrollments and certificates will be permanently removed. This cannot be undone.`,
                                    destructive: true,
                                }),
                        },
                    ]}
                />
            )}

            {/* Bulk confirm */}
            <AlertDialog open={!!bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(null)}>
                <AlertDialogContent data-testid="bulk-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle
                            className={`font-display ${
                                bulkConfirm?.destructive ? "text-destructive" : ""
                            }`}
                        >
                            {bulkConfirm?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription>{bulkConfirm?.desc}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runBulk}
                            className={
                                bulkConfirm?.destructive
                                    ? "bg-destructive hover:bg-destructive/90"
                                    : "bg-primary hover:bg-primary/90"
                            }
                            data-testid="bulk-confirm-btn"
                        >
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Single-item dialogs (unchanged) */}
            <AlertDialog
                open={!!archiveTarget}
                onOpenChange={(open) => !open && setArchiveTarget(null)}
            >
                <AlertDialogContent data-testid="archive-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            {archiveTarget?.is_archived
                                ? "Restore this program?"
                                : "Archive this program?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {archiveTarget?.is_archived ? (
                                <>
                                    <span className="font-medium text-foreground">
                                        "{archiveTarget?.title}"
                                    </span>{" "}
                                    will become visible in the public catalog again.
                                </>
                            ) : (
                                <>
                                    <span className="font-medium text-foreground">
                                        "{archiveTarget?.title}"
                                    </span>{" "}
                                    will be hidden from the public catalog. Existing enrollments
                                    keep access, and you can restore at any time.
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => archiveTarget && toggleArchive(archiveTarget)}
                            className={
                                archiveTarget?.is_archived
                                    ? "bg-primary hover:bg-primary/90"
                                    : "bg-destructive hover:bg-destructive/90"
                            }
                            data-testid="archive-confirm-btn"
                        >
                            {archiveTarget?.is_archived ? "Yes, restore" : "Yes, archive"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
            >
                <AlertDialogContent data-testid="delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            Move this program to trash?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                "{deleteTarget?.title}"
                            </span>{" "}
                            will be hidden from the public catalog and student views. You can
                            restore it from the <strong>Trash</strong> tab at any time.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (deleteTarget) {
                                    singleAction(
                                        `/admin/programs/${deleteTarget.id}`,
                                        "delete",
                                        "Moved to trash",
                                    );
                                    setDeleteTarget(null);
                                }
                            }}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="delete-confirm-btn"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Move to trash
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!restoreTarget}
                onOpenChange={(open) => !open && setRestoreTarget(null)}
            >
                <AlertDialogContent data-testid="restore-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            Restore this program?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                "{restoreTarget?.title}"
                            </span>{" "}
                            will be restored to your Active programs. It will remain unpublished
                            until you publish it again.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (restoreTarget) {
                                    singleAction(
                                        `/admin/programs/${restoreTarget.id}/restore`,
                                        "post",
                                        "Program restored",
                                    );
                                    setRestoreTarget(null);
                                }
                            }}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="restore-confirm-btn"
                        >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Yes, restore
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!purgeTarget}
                onOpenChange={(open) => !open && setPurgeTarget(null)}
            >
                <AlertDialogContent data-testid="purge-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-destructive">
                            Delete this program forever?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                "{purgeTarget?.title}"
                            </span>{" "}
                            and all of its chapters, lessons, quizzes, enrollments and certificates
                            will be permanently removed.{" "}
                            <strong className="text-destructive">This cannot be undone.</strong>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (purgeTarget) {
                                    singleAction(
                                        `/admin/programs/${purgeTarget.id}/purge`,
                                        "delete",
                                        "Program permanently deleted",
                                    );
                                    setPurgeTarget(null);
                                }
                            }}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="purge-confirm-btn"
                        >
                            <Ban className="h-4 w-4 mr-2" />
                            Delete forever
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ProgramScheduleDialog
                program={scheduleProgram}
                onClose={(saved) => {
                    setScheduleProgram(null);
                    if (saved) load();
                }}
            />
        </div>
    );
}
